/**
 * Session-scoped persistence for the in-chat theme request card, so a page refresh doesn't
 * discard an open form or what the user typed into it. Per dashboard, and sessionStorage
 * rather than localStorage: a refresh restores the card, a new tab starts clean.
 *
 * The attached File cannot be serialized — only the text fields survive a refresh.
 */

const KEY_PREFIX = "coeff.webDashboardThemeRequest.";

export interface ThemeRequestDraft {
  /** The card is open in the chat. */
  open: boolean;
  description: string;
  brandUrl: string;
}

const keyFor = (dashboardId?: string): string | null =>
  dashboardId ? `${KEY_PREFIX}${dashboardId}` : null;

export function readThemeRequestDraft(dashboardId?: string): ThemeRequestDraft | null {
  const key = keyFor(dashboardId);
  if (!key || typeof window === "undefined") {
    return null;
  }
  const raw = window.sessionStorage.getItem(key);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<ThemeRequestDraft>;
    return {
      open: parsed.open === true,
      description: typeof parsed.description === "string" ? parsed.description : "",
      brandUrl: typeof parsed.brandUrl === "string" ? parsed.brandUrl : "",
    };
  } catch (error) {
    if (error instanceof SyntaxError) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    throw error;
  }
}

export function writeThemeRequestDraft(
  dashboardId: string | undefined,
  draft: Partial<ThemeRequestDraft>
): void {
  const key = keyFor(dashboardId);
  if (!key || typeof window === "undefined") {
    return;
  }
  const current = readThemeRequestDraft(dashboardId);
  window.sessionStorage.setItem(
    key,
    JSON.stringify({
      open: draft.open ?? current?.open ?? true,
      description: draft.description ?? current?.description ?? "",
      brandUrl: draft.brandUrl ?? current?.brandUrl ?? "",
    })
  );
}

export function clearThemeRequestDraft(dashboardId?: string): void {
  const key = keyFor(dashboardId);
  if (!key || typeof window === "undefined") {
    return;
  }
  window.sessionStorage.removeItem(key);
}
